import { Banner } from '@/app/previewComponents/content-page/common/banner';
import { Intro } from '@/app/previewComponents/content-page/common/intro';
import { Video } from '@/app/previewComponents/content-page/variations/banners-with-video-and-external-link';
import { renderPhraseString } from '@/app/previewComponents/util';
import { PhraseKeysCategory } from '@/config/phrase-contract';
import {
  ContentPageSectionVisibility,
  PlannerConfigEntity,
} from '@/services/configurator/PlannerConfigTypes';
import { ContentPageVariant } from '@pnpmworkspace/shared-types';
import { JSX } from 'react';

type ComponentProps = {
  phraseStrings: { [key: string]: string };
  contentPageProps: Partial<PlannerConfigEntity>;
  dynamicContentPageSectionVisibility?: ContentPageSectionVisibility;
  contentPageVariant?: ContentPageVariant;
};

const COMPONENT_MAP: Partial<
  Record<PhraseKeysCategory, (props: ComponentProps) => JSX.Element>
> = {
  'page-intro': (props: ComponentProps) => (
    <Intro
      title={renderPhraseString('page-intro-title', props.phraseStrings)}
      description={renderPhraseString(
        'page-intro-description',
        props.phraseStrings,
      )}
      className='w-1/2'
    />
  ),
  'page-primary-banner': (props: ComponentProps) => (
    <Banner
      title={
        renderPhraseString('page-primary-banner-title', props.phraseStrings) ||
        renderPhraseString('planner.fallback-title', props.phraseStrings)
      }
      description={
        renderPhraseString(
          'page-primary-banner-description',
          props.phraseStrings,
        ) || renderPhraseString('large-description', props.phraseStrings)
      }
      cta={
        renderPhraseString('page-primary-banner-cta', props.phraseStrings) ||
        renderPhraseString('planner.fallback-cta', props.phraseStrings)
      }
      image={
        props.contentPageProps.imageUrl ||
        renderPhraseString(
          'page-primary-banner-img-url-fallback',
          props.phraseStrings,
        )
      }
      alt={renderPhraseString(
        'page-primary-banner-alt-text',
        props.phraseStrings,
      )}
      linkTo={props.contentPageProps.plannerUrl || ''}
      loading='eager'
      componentType='planner_banner'
      testId='plann-primary-banner'
      contentPageVariant={props.contentPageVariant}
      className='w-1/2'
    />
  ),
  'page-secondary-banner': (props: ComponentProps) => (
    <>
      {props.dynamicContentPageSectionVisibility &&
        props.dynamicContentPageSectionVisibility.pageSecondaryBanner && (
          <Banner
            title={renderPhraseString(
              'page-secondary-banner-title',
              props.phraseStrings,
            )}
            description={renderPhraseString(
              'page-secondary-banner-description',
              props.phraseStrings,
            )}
            cta={renderPhraseString(
              'page-secondary-banner-cta',
              props.phraseStrings,
            )}
            image={props.contentPageProps.secondaryBannerImgUrl}
            alt={renderPhraseString(
              'page-secondary-banner-img-alt-text',
              props.phraseStrings,
            )}
            // should come from marketData
            linkTo='testLink'
            componentType='services_banner'
            contentPageVariant={props.contentPageVariant}
            className='w-1/2'
          />
        )}
    </>
  ),
  'page-primary-card': (props: ComponentProps) => (
    <>
      {props.dynamicContentPageSectionVisibility &&
        props.dynamicContentPageSectionVisibility.pagePrimaryCard && (
          <Banner
            title={renderPhraseString(
              'page-primary-card-title',
              props.phraseStrings,
            )}
            description={renderPhraseString(
              'page-primary-card-description',
              props.phraseStrings,
            )}
            cta={renderPhraseString(
              'page-primary-card-cta',
              props.phraseStrings,
            )}
            linkTo='page-primary-card-cta-url'
            componentType='learn_more_banner'
            contentPageVariant={props.contentPageVariant}
            className='w-1/2'
          />
        )}
    </>
  ),
  'page-secondary-card': (props: ComponentProps) => (
    <>
      {props.dynamicContentPageSectionVisibility &&
        props.dynamicContentPageSectionVisibility.pageSecondaryCard && (
          <Banner
            title={renderPhraseString(
              'page-secondary-card-title',
              props.phraseStrings,
            )}
            description={renderPhraseString(
              'page-secondary-card-description',
              props.phraseStrings,
            )}
            cta={renderPhraseString(
              'page-secondary-card-cta',
              props.phraseStrings,
            )}
            linkTo='page-secondary-card-cta-url'
            componentType='learn_more_banner'
            contentPageVariant={props.contentPageVariant}
            className='w-1/2'
          />
        )}
    </>
  ),
  video: (props: ComponentProps) => (
    <>
      {props.dynamicContentPageSectionVisibility &&
        props.dynamicContentPageSectionVisibility['video'] && (
          <div className='w-1/2'>
            <Video
              videoUrl='https://www.ikea.com/ext/ingkadam/m/7c5c2e0b5b3d12cb/original/692511_EKET_WEB_1x1.mp4'
              videoDescription={renderPhraseString(
                'videoDescription',
                props.phraseStrings,
              )}
              videoTitle={renderPhraseString('videoTitle', props.phraseStrings)}
            />
          </div>
        )}
    </>
  ),
};

export const renderPreviewComponent = (
  category: PhraseKeysCategory,
  dynamicPhraseValues: { [key: string]: string },
  contentPageProps: Partial<PlannerConfigEntity>,
  dynamicContentPageSectionVisibility?: ContentPageSectionVisibility,
  contentPageVariant?: ContentPageVariant,
) => {
  const Component = COMPONENT_MAP[category];
  return Component
    ? Component({
        phraseStrings: dynamicPhraseValues,
        contentPageProps,
        dynamicContentPageSectionVisibility,
        contentPageVariant,
      })
    : null;
};
